'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { track } from '@/lib/analytics';
import type { FeedbackPromptTrigger } from '@/lib/analytics/events';
import { usePlayheadDistance } from '@/contexts/studio-context';
import { useShortViewport } from '@/hooks/use-short-viewport';
import type { JourneySnapshot } from '@/lib/learning-journey/journey';
import {
  hasPromptBeenShown,
  recordPromptOutcome,
} from '@/components/ui/organisms/practice-feedback-prompt/prompt-storage';
import { PROMPT_DELAY_MS } from '@/components/ui/organisms/practice-feedback-prompt/feedback-prompt-palette';

/**
 * Décide *quand* demander un retour, et rien d'autre.
 *
 * # Le moment, et pourquoi celui-là
 *
 * On ne peut pas s'accrocher à la fin de session : `practice_session_ended`
 * n'est émis qu'au démontage de l'overlay ou à la fermeture de l'onglet, donc
 * toujours au moment où la surface qui porterait l'invitation disparaît.
 *
 * Le seul instant où « je viens de réussir » et « je ne suis en train de rien
 * faire » sont vrais en même temps, c'est la **frontière d'étape** : quand la
 * FSM dépasse une phrase validée, `learn-piece-page` met le transport en pause
 * et fait défiler le roll vers la suivante. Les mains ont quitté les touches,
 * rien ne joue, la personne est là. C'est l'expiration du cycle.
 *
 * # Pourquoi la deuxième phrase et pas la première
 *
 * La première validation porte sur le tutoriel : la personne découvre le
 * mécanisme, et son avis est « c'est joli » ou « je n'ai rien compris ». La
 * deuxième prouve que la boucle a tourné deux fois, donc qu'elle a une opinion
 * sur *la méthode* et non sur un écran. C'est la différence entre un retour
 * décoratif et un retour qu'on peut mettre dans un ticket.
 *
 * La durée plancher couvre l'autre bord : un morceau facile peut donner deux
 * phrases en quarante secondes, et quatre minutes garantissent qu'il y a eu de
 * la friction quelque part.
 */

/** Deux phrases validées : la boucle a tourné deux fois. */
const MIN_VALIDATED = 2;

/** Quatre minutes : il s'est passé quelque chose, pas seulement deux clics. */
const MIN_SESSION_MS = 4 * 60 * 1000;

/**
 * Place qu'il faut sous la playhead pour poser la capsule sans recouvrir des
 * notes à venir : sa hauteur, sa marge basse, et un peu d'air.
 */
const REQUIRED_ROOM_PX = 60;

export type UseFeedbackPromptOptions = {
  readonly snapshot: JourneySnapshot | null;
};

export type UseFeedbackPromptResult = {
  readonly visible: boolean;
  readonly trigger: FeedbackPromptTrigger;
  /** Consigne la disparition et ferme définitivement la porte. */
  readonly dismiss: (reason: 'closed' | 'resumed' | 'left', hadDraft: boolean) => void;
  /** Consigne une réponse envoyée — même porte, autre issue. */
  readonly markAnswered: () => void;
  /** Retire la carte, une fois le remerciement lu. */
  readonly close: () => void;
};

const TRIGGER: FeedbackPromptTrigger = 'phrase_streak';

export function useFeedbackPrompt(opts: UseFeedbackPromptOptions): UseFeedbackPromptResult {
  const { snapshot } = opts;

  const shortViewport = useShortViewport();
  const playheadDistance = usePlayheadDistance();

  const [visible, setVisible] = useState(false);

  /**
   * Zéro jusqu'au montage : `Date.now()` dans l'initialiseur de `useRef`
   * s'exécute pendant le rendu, ce qui est impur — React peut rejouer un rendu
   * et l'horodatage bougerait sous les pieds de la mesure.
   */
  const sessionStartedAtRef = useRef(0);
  const prevPhraseIndexRef = useRef<number | null>(null);
  /** L'occasion est unique : une fois consommée, plus rien ne la rouvre. */
  const spentRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Lu par le nettoyage, qui ne doit consigner « left » que si l'invitation était à l'écran. */
  const visibleRef = useRef(false);
  /**
   * L'invitation a déjà reçu son sort — refusée ou remplie.
   *
   * Sans ce drapeau, quitter le parcours juste après un envoi émettrait un
   * abandon par-dessus la réponse : `setVisible(false)` ne se répercute sur
   * `visibleRef` qu'au rendu suivant, et un démontage dans la même passe
   * lirait encore `true`.
   */
  const settledRef = useRef(false);

  // Les valeurs qui accompagnent `feedback_prompt_shown` sont figées au moment
  // de l'armement : les lire dans le minuteur donnerait celles de 1400 ms plus
  // tard, et le lien avec la réussite qui a déclenché l'invitation serait
  // faussé.
  const pendingRef = useRef<{ phrasesValidated: number; sessionDurationMs: number } | null>(null);

  // Déclaré avant l'armement pour que l'horodatage soit posé quand celui-ci
  // s'exécute : les effets partent dans leur ordre de déclaration.
  useEffect(() => {
    sessionStartedAtRef.current = Date.now();
  }, []);

  useEffect(() => {
    visibleRef.current = visible;
  }, [visible]);

  // ── Armement ────────────────────────────────────────────────────
  useEffect(() => {
    if (snapshot === null || spentRef.current || timerRef.current !== null) {
      return;
    }

    const idx = snapshot.currentPhraseIndex;
    const prev = prevPhraseIndexRef.current;
    prevPhraseIndexRef.current = idx;

    // Seule une *transition* est une frontière d'étape. Le premier rendu n'en
    // est pas une : personne n'a encore rien réussi.
    if (prev === null || prev === idx) {
      return;
    }

    const phrasesValidated = snapshot.phraseProgress.filter(
      (progress) => progress.etatMax === 'DONE',
    ).length;

    if (phrasesValidated < MIN_VALIDATED) {
      return;
    }

    if (sessionStartedAtRef.current === 0) {
      return;
    }

    const sessionDurationMs = Date.now() - sessionStartedAtRef.current;

    if (sessionDurationMs < MIN_SESSION_MS) {
      return;
    }

    // Un téléphone couché n'a pas la place de porter la carte dépliée, et
    // brûler l'unique occasion sur un écran où l'on ne peut pas répondre
    // confortablement serait du gaspillage pur. On ne consigne rien : la
    // capsule pourra apparaître plus tard sur un écran normal.
    if (shortViewport) {
      return;
    }

    // Idem si la bande sous la playhead est trop courte — on attend la
    // frontière suivante plutôt que de poser la capsule sur des notes à venir.
    if (playheadDistance > 0 && playheadDistance < REQUIRED_ROOM_PX) {
      return;
    }

    if (hasPromptBeenShown()) {
      // Déjà vue sur ce navigateur — ou stockage inaccessible, ce qui se lit
      // pareil. On marque l'occasion consommée pour ne plus retester à chaque
      // frontière.
      spentRef.current = true;

      return;
    }

    pendingRef.current = { phrasesValidated, sessionDurationMs };

    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      spentRef.current = true;

      const pending = pendingRef.current;

      if (pending === null) {
        return;
      }

      // L'écriture a lieu ici, à l'affichage, et non à la réponse : le cas le
      // plus probable est l'onglet fermé pendant que la capsule est à l'écran,
      // et il doit compter comme « déjà demandé ».
      recordPromptOutcome('shown');
      setVisible(true);

      track({
        name: 'feedback_prompt_shown',
        properties: { trigger: TRIGGER, ...pending },
      });
    }, PROMPT_DELAY_MS);
  }, [snapshot, shortViewport, playheadDistance]);

  // ── Nettoyage ───────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }

      if (visibleRef.current && !settledRef.current) {
        // Le parcours a été quitté sans que l'invitation soit ni refusée ni
        // remplie. Ne rien faire est une réponse, et elle se respecte comme
        // les autres : la clé est déjà posée, on ne redemandera pas.
        track({
          name: 'feedback_prompt_dismissed',
          properties: { trigger: TRIGGER, reason: 'left', hadDraft: false },
        });
      }
    };
  }, []);

  const dismiss = useCallback(
    (reason: 'closed' | 'resumed' | 'left', hadDraft: boolean) => {
      if (settledRef.current) {
        return;
      }

      settledRef.current = true;
      setVisible(false);
      recordPromptOutcome('dismissed');

      track({
        name: 'feedback_prompt_dismissed',
        properties: { trigger: TRIGGER, reason, hadDraft },
      });
    },
    [],
  );

  /**
   * Appelée dès que l'envoi a réussi, et non à la fin du remerciement : si
   * l'onglet se ferme pendant les trois secondes de « Reçu. Merci. », la
   * réponse a bien eu lieu et la clé doit le dire.
   */
  const markAnswered = useCallback(() => {
    settledRef.current = true;
    recordPromptOutcome('answered');
  }, []);

  /** Retire la carte de l'écran, sans rien consigner de plus. */
  const close = useCallback(() => {
    setVisible(false);
  }, []);

  return { visible, trigger: TRIGGER, dismiss, markAnswered, close };
}
